import { typeSafeObjectKeys } from '@luminovo/commons';
import { MenuItem } from '@luminovo/design-system';
import { languageTranslations, TransEnum, transEnum } from '@luminovo/frontend-utils';
import { Menu } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { useCurrentUserDetailsContext } from '../data/contexts/CurrentUserDetailsContext';
import { useHttpMutation } from '../data/mutation/useHttpMutation';
import { getIconForLanguage } from '../utils/getIconForLanguage';

// The flags are drawn at 18px: sized like this they fill the menu item's 16px icon slot, not a corner of it.
const FILL_ICON_SLOT = { width: '100%', height: '100%' };

/** The language row of an account menu; its submenu sits beside the row, as `MenuItem` renders only label and icon. */
export const LanguageSelection: React.FunctionComponent = () => {
    const queryClient = useQueryClient();
    const { user } = useCurrentUserDetailsContext();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const { mutateAsync } = useHttpMutation('PATCH /my/user', {
        snackbarMessage: null,
        onSuccess: () => {
            // Experiment against spinners that stayed up after a language change; unclear whether it helps.
            queryClient.clear();
            // Intercom doesn't pick up the new language, so reload the whole app rather than just the query cache.
            window.location.reload();
        },
    });

    return (
        <>
            <MenuItem
                label={<TransEnum text={user.user_language} translations={languageTranslations} />}
                startIcon={getIconForLanguage(user.user_language, FILL_ICON_SLOT)}
                onClick={(event: React.MouseEvent<HTMLLIElement>) => setAnchorEl(event.currentTarget)}
            />
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                    paper: {
                        style: {
                            transform: 'translateX(-100%)',
                        },
                    },
                }}
            >
                {typeSafeObjectKeys(languageTranslations).map((key) => (
                    <MenuItem
                        key={key}
                        label={transEnum(key, languageTranslations)}
                        startIcon={getIconForLanguage(key, FILL_ICON_SLOT)}
                        onClick={() => {
                            setAnchorEl(null);
                            mutateAsync({ requestBody: { user_language: key } });
                        }}
                    />
                ))}
            </Menu>
        </>
    );
};
